import classNames from "classnames/dedupe";
import React, { FC, MouseEvent, useCallback, useMemo, ReactNode } from "react";

import useWatchState from "../../../hooks/useWatchState";
import { useTheme } from "../../Utility/ThemeProvider";
import { accordionContext as AccordionContext } from "./AccordionContext";
import { TOnToggleAccordion } from "../../Navigational/Filters/Filter.interface";

export interface IAccordionItemProps {
  id?: string | number;
  /**
   * Controls Accordion expanded state
   */
  isExpanded?: boolean;
  /**
   * The custom set of css classes used to style component
   */
  classes?: { root?: string; toggled?: string };
  children?: ReactNode;
  /**
   * Callback function on when Accordion title has been clicked or expanded state has changed
   */
  radioButtonAccordion?: boolean;
  onToggle?: TOnToggleAccordion;
}

const AccordionItem: FC<IAccordionItemProps> = ({
  id,
  isExpanded,
  onToggle,
  children,
  classes,
  radioButtonAccordion = false,
}) => {
  const [expanded, setExpanded] = useWatchState<boolean>({ value: isExpanded });

  const toggleAccordion = useCallback(
    (event: MouseEvent<HTMLButtonElement>) => {
      onToggle?.(id, !expanded, event);
      setExpanded(!expanded);
    },
    [id, expanded, setExpanded, onToggle],
  );

  const handleToggle = useCallback(
    (event: MouseEvent<HTMLButtonElement>) => {
      if (radioButtonAccordion) {
        !isExpanded && toggleAccordion(event);
      } else {
        toggleAccordion(event);
      }
    },
    [isExpanded, radioButtonAccordion, toggleAccordion],
  );
  const theme = useTheme();

  const context = useMemo(
    () => ({
      id,
      expanded,
      onToggle: handleToggle,
      theme,
    }),
    [id, expanded, handleToggle, theme],
  );

  const rootClasses = classNames(classes?.root, {
    [classes?.toggled]: expanded,
  });

  return (
    <AccordionContext.Provider value={context}>
      <div className={rootClasses}>{children}</div>
    </AccordionContext.Provider>
  );
};

export default AccordionItem;
